const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
export const V3_FACTOR_SLOT_MS = 30 * 60 * 1000;

// Alpha keeps the customer's strategy visually anchored to bronze across the
// dashboard and expanded comparison, while actual performance stays ink.
export const ALPHA_PERFORMANCE_SERIES_STROKES = {
  strategy: "var(--rp-amber)",
  actual: "currentColor",
} as const;

export const V3_TIME_FILTER_OPTIONS = [
  { value: "1 day", label: "1D" },
  { value: "3 days", label: "3D" },
  { value: "1 week", label: "1W" },
  { value: "1 month", label: "1M" },
  { value: "3 months", label: "3M" },
  { value: "Year to date", label: "YTD" },
  { value: "1 year", label: "1Y" },
  { value: "All", label: "All" },
  { value: "Since inception", label: "Inception" },
] as const;

export const V3_TODAY_TIME_FILTER_OPTION = { value: "Today", label: "Today" } as const;

export const V3_INVESTOR_TIME_FILTER_OPTIONS = [
  V3_TODAY_TIME_FILTER_OPTION,
  ...V3_TIME_FILTER_OPTIONS,
] as const;

export type V3TimeFilter = (typeof V3_INVESTOR_TIME_FILTER_OPTIONS)[number]["value"];

const WINDOW_MS_BY_FILTER: Partial<Record<V3TimeFilter, number>> = {
  "1 day": DAY_MS,
  "3 days": DAY_MS * 3,
  "1 week": DAY_MS * 7,
  "1 month": DAY_MS * 30,
  "3 months": DAY_MS * 90,
  "1 year": DAY_MS * 365,
};

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function getEarliestCoveredStartMs(earliestFactorDate: string | null): number | null {
  const earliestFactor = parseDate(earliestFactorDate);
  if (!earliestFactor) return null;
  return earliestFactor.getTime() - V3_FACTOR_SLOT_MS;
}

export function getUtcYearStart(anchor: Date): Date {
  return new Date(Date.UTC(anchor.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
}

export function getLocalTodayStart(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
}

export function getV3PeriodStart(period: V3TimeFilter, anchor: Date): Date | null {
  if (period === "All" || period === "Since inception") return null;

  if (period === "Today") {
    return getLocalTodayStart();
  }

  if (period === "Year to date") {
    return getUtcYearStart(anchor);
  }

  const windowMs = WINDOW_MS_BY_FILTER[period];
  if (!windowMs) return null;
  return new Date(anchor.getTime() - windowMs);
}

export function getV3FilterWindowBounds(
  period: V3TimeFilter,
  anchor: Date,
  entityCreateDate: string | null = null
): { start: Date | null; end: Date } {
  if (period === "Since inception") {
    return { start: parseDate(entityCreateDate), end: anchor };
  }

  if (period === "Today") {
    const todayStart = getLocalTodayStart();
    const entityStart = parseDate(entityCreateDate);
    return {
      start: entityStart && entityStart > todayStart ? entityStart : todayStart,
      end: anchor,
    };
  }

  return {
    start: getV3PeriodStart(period, anchor),
    end: anchor,
  };
}

export function hasV3CoverageForPeriod(
  period: V3TimeFilter,
  earliestFactorDate: string | null,
  anchor: Date,
  entityCreateDate: string | null = null
): boolean {
  if (period === "All" || period === "Since inception") {
    return earliestFactorDate !== null;
  }

  const earliestCoveredStartMs = getEarliestCoveredStartMs(earliestFactorDate);
  if (earliestCoveredStartMs === null) return false;

  const { start } = getV3FilterWindowBounds(period, anchor, entityCreateDate);
  if (!start) return true;

  return earliestCoveredStartMs <= start.getTime();
}

export function getV3AvailableFilters(
  earliestFactorDate: string | null,
  anchor: Date = new Date()
): V3TimeFilter[] {
  const available: V3TimeFilter[] = [];

  V3_TIME_FILTER_OPTIONS.forEach((option) => {
    if (option.value === "Since inception") {
      available.push(option.value);
      return;
    }

    if (option.value === "All") {
      if (earliestFactorDate) {
        available.push(option.value);
      }
      return;
    }

    if (hasV3CoverageForPeriod(option.value, earliestFactorDate, anchor)) {
      available.push(option.value);
    }
  });

  if (available.length === 0) return ["Since inception"];
  return available;
}

export function getV3DefaultFilter(availableFilters: V3TimeFilter[]): V3TimeFilter {
  const preferredFilters: V3TimeFilter[] = [
    "1 year",
    "Year to date",
    "3 months",
    "1 month",
    "1 week",
    "3 days",
    "1 day",
    "Since inception",
    "All",
  ];

  const match = preferredFilters.find((period) => availableFilters.includes(period));
  if (match) return match;

  if (availableFilters.includes("Since inception")) return "Since inception";
  return availableFilters[0] ?? "Since inception";
}

export function getV3RangeForFilter(
  period: V3TimeFilter,
  anchor: Date = new Date(),
  entityCreateDate: string | null = null
): { from?: string; to?: string } {
  const to = anchor.toISOString();

  if (period === "All") {
    return { to };
  }

  const { start } = getV3FilterWindowBounds(period, anchor, entityCreateDate);
  return {
    from: start?.toISOString(),
    to,
  };
}

export function getBucketMsForFilter(period: V3TimeFilter): number {
  switch (period) {
    case "Today":
    case "1 day":
    case "3 days":
      return V3_FACTOR_SLOT_MS;
    case "1 week":
      return 60 * 60 * 1000;
    case "1 month":
      return 4 * 60 * 60 * 1000;
    default:
      return DAY_MS;
  }
}

/**
 * Parse a user-config `launch_date` (e.g. "2026/04/01", "2026-04-01", or an ISO
 * timestamp) into the UTC-midnight epoch ms of that calendar day. Factor series
 * are UTC-stamped (strategy at 30-minute UTC slots, actual at
 * `${date}T00:00:00.000Z`), so anchoring to UTC midnight keeps the launch day's
 * own factors on the kept side of a clip. Returns null for empty/invalid input
 * (callers treat null as "no launch anchor").
 */
export function parseLaunchDateUtcMs(launchDate?: string | null): number | null {
  if (!launchDate) return null;
  const trimmed = launchDate.trim();
  if (trimmed === "") return null;

  const ymd = trimmed.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (ymd) {
    const year = Number(ymd[1]);
    const month = Number(ymd[2]);
    const day = Number(ymd[3]);
    const ms = Date.UTC(year, month - 1, day);
    if (Number.isNaN(ms)) return null;
    // Date.UTC silently rolls overflow (e.g. 2026/02/31 -> Mar 3, 2026/13/01 ->
    // Jan 2027). Reject anything that did not round-trip so an invalid calendar
    // date fails open (no clip) rather than anchoring to a different day.
    const rt = new Date(ms);
    if (
      rt.getUTCFullYear() !== year ||
      rt.getUTCMonth() !== month - 1 ||
      rt.getUTCDate() !== day
    ) {
      return null;
    }
    return ms;
  }

  const fallback = new Date(trimmed).getTime();
  return Number.isNaN(fallback) ? null : fallback;
}

/**
 * Resolve the launch-date clip boundary (UTC-midnight ms) for a config
 * `launch_date`. Returns null when there is no valid launch date OR when the
 * launch date is still in the future, so a not-yet-reached launch keeps full
 * history until it passes (see spec decision 4). `nowMs` is injectable for
 * deterministic tests; it defaults to the current time.
 */
export function resolveLaunchClipMs(
  launchDate?: string | null,
  nowMs: number = Date.now()
): number | null {
  const launchMs = parseLaunchDateUtcMs(launchDate);
  if (launchMs === null) return null;
  return launchMs <= nowMs ? launchMs : null;
}

/**
 * Drop every factor dated before `launchMs` (UTC midnight of the launch day),
 * keeping the launch day's own factors and everything after. Pure; returns the
 * input array unchanged when `launchMs` is null (no launch anchor). Used to
 * anchor a customer's computed/actual performance to their launch date so
 * pre-launch history is ignored across every window and metric.
 */
export function clipFactorsFromLaunch<T extends { t: string }>(
  factors: T[],
  launchMs: number | null
): T[] {
  if (launchMs === null) return factors;
  return factors.filter((factor) => {
    const ms = new Date(factor.t).getTime();
    return !Number.isNaN(ms) && ms >= launchMs;
  });
}

/**
 * Flat platform fee applied to the product line in the detail-page
 * "Net & Benchmark" chart view. See applyAnnualFeeToCumulativeSeries.
 */
export const DETAIL_FEE_ANNUAL = 0.01;

const MS_PER_YEAR = 365 * DAY_MS;

export interface CumulativeSeriesPoint {
  date: string;
  fullDate: string;
  performance: number;
}

/**
 * Buckets already-window-filtered factors by `bucketMs`, compounds within each
 * bucket, accumulates, and rebases to 0% at `chartStartMs` (prepended baseline).
 *
 * This is the single source of truth for the V3 cumulative-series math: it is
 * used both by usePerformanceChartData (the gross product line) and by the
 * detail-page benchmark overlay, so the product and benchmark lines bucket
 * identically. Pure and order-independent (bucket products are commutative).
 */
export function bucketFactorsToCumulativeSeries(
  factors: Array<{ t: string; f: number }>,
  chartStartMs: number,
  bucketMs: number
): CumulativeSeriesPoint[] {
  if (factors.length === 0) return [];

  const bucketFactors = new Map<number, number>();
  for (const factor of factors) {
    const ts = new Date(factor.t).getTime();
    if (Number.isNaN(ts)) continue;
    const bucketIndex = Math.max(1, Math.ceil((ts - chartStartMs) / bucketMs));
    const bucketTs = chartStartMs + bucketIndex * bucketMs;
    const currentFactor = bucketFactors.get(bucketTs) ?? 1;
    bucketFactors.set(bucketTs, currentFactor * factor.f);
  }

  const bucketEntries = [...bucketFactors.entries()].sort((a, b) => a[0] - b[0]);
  let cumulative = 1;
  const cumulativePoints = bucketEntries.map(([bucketTs, bucketFactor]) => {
    cumulative *= bucketFactor;
    const iso = new Date(bucketTs).toISOString();
    return {
      date: iso,
      fullDate: iso,
      performance: (cumulative - 1) * 100,
    };
  });

  if (bucketEntries.length === 0) return cumulativePoints;

  const baselineIso = new Date(chartStartMs).toISOString();
  return [{ date: baselineIso, fullDate: baselineIso, performance: 0 }, ...cumulativePoints];
}

export interface NetFeeSeriesPoint {
  date: string;
  fullDate?: string;
  performance?: number | null;
  performanceNet: number;
}

/**
 * Derives a net-of-fees series from a gross cumulative-% series (rebased to 0%
 * at its first point). Applies a continuous annual-fee drag over the elapsed
 * time from the window start, so the fee shown reflects the displayed window
 * only. Returns each input point with an added `performanceNet` field.
 *
 *   netGrowth(t) = grossGrowth(t) * (1 - feeAnnual)^((t - startMs) / year)
 *   performanceNet = (netGrowth(t) - 1) * 100
 */
export function applyAnnualFeeToCumulativeSeries(
  grossPoints: Array<{ date: string; fullDate?: string; performance?: number | null }>,
  feeAnnual: number = DETAIL_FEE_ANNUAL
): NetFeeSeriesPoint[] {
  if (grossPoints.length === 0) return [];

  const startMs = new Date(grossPoints[0].fullDate ?? grossPoints[0].date).getTime();
  const dragBase = 1 - feeAnnual;

  return grossPoints.map((point) => {
    const tMs = new Date(point.fullDate ?? point.date).getTime();
    const years =
      Number.isNaN(tMs) || Number.isNaN(startMs) ? 0 : Math.max(0, (tMs - startMs) / MS_PER_YEAR);
    const drag = Math.pow(dragBase, years);
    const grossGrowth = 1 + (point.performance ?? 0) / 100;
    return {
      ...point,
      performanceNet: (grossGrowth * drag - 1) * 100,
    };
  });
}

interface MetricValuePoint {
  metricDate: string;
  metricValue: number;
}

export function computeCumulativePerformance<T extends MetricValuePoint>(
  factorMetrics: T[]
): number | null {
  if (factorMetrics.length === 0) return null;
  const sorted = [...factorMetrics].sort(
    (a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime()
  );
  let cumulative = 1;
  for (const metric of sorted) {
    cumulative *= metric.metricValue;
  }
  return (cumulative - 1) * 100;
}

/**
 * Computes a V3 factor return with the same strict window boundary used by the
 * dashboard chart. The factor exactly on the window start is the baseline and
 * is not part of the reported return.
 */
export function computeFactorPeriodReturn(
  factors: Array<{ t: string; f: number }>,
  period: V3TimeFilter,
  entityCreateDate: string | null = null
): number | null {
  if (factors.length === 0) return null;

  const sorted = [...factors]
    .filter((factor) => {
      const factorMs = new Date(factor.t).getTime();
      return !Number.isNaN(factorMs) && Number.isFinite(factor.f);
    })
    .sort((left, right) => new Date(left.t).getTime() - new Date(right.t).getTime());
  if (sorted.length === 0) return null;

  if (period === "All" || period === "Since inception") {
    return computeCumulativePerformance(
      sorted.map((factor) => ({
        metricDate: factor.t,
        metricValue: factor.f,
      }))
    );
  }

  const anchor = new Date(sorted[sorted.length - 1].t);
  const { start, end } = getV3FilterWindowBounds(period, anchor, entityCreateDate);
  const startMs = start?.getTime() ?? Number.NEGATIVE_INFINITY;
  const endMs = end.getTime();
  return computeCumulativePerformance(
    sorted
      .filter((factor) => {
        const factorMs = new Date(factor.t).getTime();
        return factorMs > startMs && factorMs <= endMs;
      })
      .map((factor) => ({
        metricDate: factor.t,
        metricValue: factor.f,
      }))
  );
}
